import { useStaticQuery } from "gatsby";
import React from "react";
import { useTranslation } from "react-i18next";
import { text } from "../../container/language";
import { buildURLForMenuItem } from "../../utils/buildURL";
import Link from "../../utils/Link";
import { usePageContext } from "../../utils/PageContext";
import { useCollapsible } from "../Header/elements/CategoriesNav";
import "./syle.scss";

export default function Footer() {
  const apiData = useStaticQuery(query);
  const social = apiData.api.configSocialMediaAndApp;
  const contact = apiData.api.configContact;

  const { t } = useTranslation();
  const { lang, articleType } = usePageContext();

  const isBusiness = articleType === "BUSINESS";
  const isAboutUs = articleType === "ABOUT_US";
  const isPersonal = !isBusiness && !isAboutUs;

  const individualCategories = apiData.api.menu.menus;
  const businessCategories = (apiData.api.businessMenu || {}).menus || [];
  const aboutUsCategories = (apiData.api.aboutUsMenu || {}).menus || [];
  const selectedArticles = apiData.api.configSelectedArticle;

  const categories =
    articleType === "BUSINESS"
      ? businessCategories
      : articleType === "ABOUT_US"
      ? aboutUsCategories
      : individualCategories;

  const data = {
    lists: [
      {
        header: {
          text: t("footer.products.title"),
          link: "/",
        },
        items: [
          ...categories.map(({ label: item }) => {
            return {
              text: text(item.label, lang),
              link: buildURLForMenuItem(item),
            };
          }),
          {
            text: "PSD 2 Open Banking",
            link: "/dokumentet/psd-2-open-banking",
          },
        ],
      },
      {
        header: {
          text: t("footer.tools.title"),
          link: "/tools",
        },
        items: [
          {
            text: t("tools.contact"),
            link: "/tools/client-services/",
          },
          {
            text: t("tools.exchange"),
            link: "/tools/exchange",
          },
          {
            text: t("tools.loan"),
            link: "/tools/loan-application",
          },
          {
            text: t("tools.creditCard"),
            link: "/tools/credit-card-application",
          },
          {
            text: t("tools.deposit"),
            link: "/tools/deposit-calculator",
          },
          {
            text: t("tools.bond"),
            link: "/tools/bonds",
          },
          {
            text: t("tools.bill"),
            link: "/tools/obligations",
          },
        ],
      },
      {
        header: {
          text: t("footer.others.title"),
          link: "/",
        },
        items: [
          {
            text: t("footer.others.personalData"),
            link: buildURLForMenuItem({
              type: "Article",
              article: selectedArticles.personalData,
            }),
          },
          {
            text: t("footer.others.cookies"),
            link: buildURLForMenuItem({
              type: "Article",
              article: selectedArticles.cookies,
            }),
          },
          {
            text: t("footer.others.legalInformation"),
            link: buildURLForMenuItem({
              type: "Article",
              article: selectedArticles.legalInformation,
            }),
          },
          {
            text: t("footer.others.covid2019"),
            link: buildURLForMenuItem({
              type: "Article",
              article: selectedArticles.covid2019,
            }),
          },{
            text: "Foreign Account Tax Compliance Act (FATCA)",
            link: "/akti-i-perputhshmerise-tatimore-per-llogarite-e-huaja-(fatca)",
          },
        ],
      },
    ],
  };

  const menu = [
    {
      text: `${t("pages.contact.label")}`,
      link: "/contact",
      icon: "i-phone-alt",
    },
    {
      text: t("pages.about.label"),
      link: "/about",
      icon: "i-users-alt",
    },
    {
      text: t("pages.news.label"),
      link: "/lajme",
      icon: "i-newspaper",
    },
    {
      text: t("pages.career.label"),
      link: buildURLForMenuItem(selectedArticles.career),
      icon: "i-fibank-logo",
    },
    {
      text: t("pages.propertiesForSale.label"),
      link: "/properties",
      icon: "i-home1",
    },
    {
      text: t("pages.help.label"),
      link: "/help",
      icon: "i-shield-question",
    },
  ];

  const collapsibleMoreMenu = useCollapsible(
    ["header-top__dropdown"],
    "header-top__dropdown--active"
  );

  const BUSINESS_LABEL = t("business");
  const PERSONAL_LABEL = t("personal");
  const ABOUT_US_LABEL = t("pages.about.label");

  return (
    <footer className="footer">
      <div className="wrapper">
        <div className="footer-contacts">
          <div className="footer-contacts__item">
            <div className="footer-contacts__inner">
              <div className="footer-contacts__inner-wrap">
                <div className="footer-contacts__icon">
                  <i className="i-calling"></i>
                </div>
                <div className="footer-contacts__text">
                  <div className="footer-contacts__header">
                    <p>
                      <a href={`tel:${contact.tollFree}`} className="big">
                        {contact.tollFree}
                      </a>
                      {/* или <a href="tel:*bank">*bank</a> */}
                    </p>
                  </div>
                  <p>{t("pages.contact.tollFreeDescription")}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="footer-contacts__item">
            <div className="footer-contacts__inner">
              <div className="footer-contacts__inner-wrap">
                <div className="footer-contacts__icon"></div>
                <div className="footer-contacts__text">
                  <div className="footer-contacts__header">
                    <p>
                      <a href={`tel:${contact.phoneNumber}`} className="big">
                        {contact.phoneNumber}
                      </a>
                    </p>
                    {/* <p>
                      или <a href="tel:+359 2 818 0006">+359 2 818 0006</a>
                    </p> */}
                  </div>
                  <p>{t("pages.contact.phoneNumberDescription")}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="footer-contacts__item">
            <div className="footer-contacts__inner">
              <div className="footer-contacts__inner-wrap">
                <div className="footer-contacts__icon">
                  <i className="i-envelopes"></i>
                </div>
                <div className="footer-contacts__text">
                  <div className="footer-contacts__header">
                    <p>
                      <a href={`mailto:${contact.email}`} className="big">
                        {contact.email}
                      </a>
                    </p>
                  </div>
                  <p>
                    <p>
                      {t("pages.contact.emailDescription")}{" "}
                      <Link to="/tools/client-services">
                        {t("pages.contact.form")}
                      </Link>
                    </p>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-top">
          <div class="footer-top__item">
            <div className={collapsibleMoreMenu.classes}>
              <div
                className="header-top__dropdown-header"
                onClick={() => collapsibleMoreMenu.toggle()}
              >
                {isPersonal && <div>{PERSONAL_LABEL}</div>}
                {isBusiness && <div>{BUSINESS_LABEL}</div>}
                {isAboutUs && <div>{ABOUT_US_LABEL}</div>}
              </div>
              <div
                className="header-top__dropdown-body"
                onClick={() => collapsibleMoreMenu.hide()}
              >
                <ul className="header-top__dropdown-list">
                  <li className="header-top__dropdown-list__item">
                    <Link to="/" className={isPersonal ? "active" : ""}>
                      {PERSONAL_LABEL}
                    </Link>
                  </li>
                  <li className="header-top__dropdown-list__item">
                    <Link to="/business" className={isBusiness ? "active" : ""}>
                      {BUSINESS_LABEL}
                    </Link>
                  </li>
                  <li className="header-top__dropdown-list__item">
                    <Link to="/about" className={isAboutUs ? "active" : ""}>
                      {ABOUT_US_LABEL}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          {/* <div className="footer-top__item"></div> */}
          <div className="footer-top__item">
            <ul className="footer-top__list">
              {menu.map(({ text, link }) => (
                <li className="footer-top__list-item" key={text}>
                  <Link to={link}>{text}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="footer-middle">
          <div className="footer-columns">
            {data.lists.map(({ header, items }) => (
              <div className="footer-columns__item">
                <div className="footer-columns__inner">
                  <h6 className="footer-columns__title">
                    <a href={header.link}>{header.text}</a>
                  </h6>
                  <ul className="footer-columns__list">
                    {items.map(({ text, link }) => (
                      <li className="footer-columns__list-item">
                        <Link to={link}>{text}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}

            <div className="footer-columns__item column-app">
              <div className="footer-columns__inner">
                <div className="footer-apps">
                  <div className="footer-apps__preview">
                    <img src="/images/iphone.png" alt="footer image" />
                  </div>
                  <div className="footer-apps__text">
                    <p>
                      <img src="/images/logo-fibank-white.png" alt="" />
                    </p>
                    <ul className="footer-apps__list">
                      <li className="footer-apps__list-item">
                        <a href={social.iosUrl} target="_blank">
                          <img
                            src={`/images/app_store___light_${lang}.svg`}
                            alt="App Store"
                          />
                        </a>
                      </li>
                      <li className="footer-apps__list-item">
                        <a href={social.androidUrl} target="_blank">
                          <img
                            src={`/images/google_play___light_${lang}.svg`}
                            alt="Google Play"
                          />
                        </a>
                      </li>
                    </ul>
                    <p>{t("footer.bankAnywhere")}</p>
                    <p>&nbsp;</p>
                    <p>
                      <small>{t("footer.mobileAndOnlineBanking")}</small>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="copyright">
            <div className="copyright__item copyright__item--copy">
              {t("copyright")}
            </div>
            <div className="copyright__item copyright__item--social">
              <div className="social-links">
                <a href={social.facebookUrl} target="_blank">
                  <i className="i-facebook"></i>
                </a>
                <a href={social.instagramUrl} target="_blank">
                  <i className="i-instagram"></i>
                </a>
                <a href={social.linkedinUrl} target="_blank">
                  <i className="i-linkedin"></i>
                </a>
                <a href={social.twitterUrl} target="_blank">
                  <i className="i-twitter"></i>
                </a>
                <a href={social.youtubeUrl} target="_blank">
                  <i className="i-youtube"></i>
                </a>
              </div>
            </div>
            <div className="copyright__item copyright__item--author">
              <a
                className="footer-bottom__author"
                target="_blank"
                href="https://togo.al"
              >
                {t("developedBy")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export const query = graphql`
  fragment ArticleLink on API_Article {
    slug
    category {
      ...Category
      slug
      title {
        id
        text_en
        text_sq
      }
      parentCategory {
        ...Category
        slug
        title {
          id
          text_en
          text_sq
        }
        parentCategory {
          ...Category
          slug
          title {
            id
            text_en
            text_sq
          }
          parentCategory {
            ...Category
            title {
              id
              text_en
              text_sq
            }
            slug
            parentCategory {
              ...Category
              slug
              title {
                id
                text_en
                text_sq
              }
            }
          }
        }
      }
    }
  }

  query {
    api {
      configSelectedArticle {
        personalData {
          ...ArticleLink
        }
        cookies {
          ...ArticleLink
        }
        legalInformation {
          ...ArticleLink
        }
        career {
          ...ArticleLink
        }
        covid2019 {
          ...ArticleLink
        }
      }

      configSocialMediaAndApp {
        facebookUrl
        androidUrl
        instagramUrl
        iosUrl
        linkedinUrl
        twitterUrl
        youtubeUrl
      }

      configContact {
        phoneNumber
        tollFree
        email
      }

      menu {
        id
        menus {
          id
          label {
            type
            uri
            article {
              slug
              category {
                ...ArticleCategoryFragment
              }
            }
            category {
              slug
              parentCategory {
                ...Category
                slug
                title {
                  id
                  text_en
                  text_sq
                }
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    title {
                      id
                      text_en
                      text_sq
                    }
                    slug
                    parentCategory {
                      ...Category
                      slug
                      title {
                        id
                        text_en
                        text_sq
                      }
                    }
                  }
                }
              }
            }
            label {
              id
              text_sq
              text_en
            }
          }
          image {
            url
          }
          columns {
            id
            head {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
            body {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
          }
        }
      }
      businessMenu: configMenuBusiness {
        id
        menus {
          id
          label {
            type
            uri
            article {
              slug
              category {
                ...ArticleCategoryFragment
              }
            }
            category {
              slug
              parentCategory {
                ...Category
                slug
                title {
                  id
                  text_en
                  text_sq
                }
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    title {
                      id
                      text_en
                      text_sq
                    }
                    slug
                    parentCategory {
                      ...Category
                      slug
                      title {
                        id
                        text_en
                        text_sq
                      }
                    }
                  }
                }
              }
            }
            label {
              id
              text_sq
              text_en
            }
          }
          image {
            url
          }
          columns {
            id
            head {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
            body {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
          }
        }
      }
      aboutUsMenu: configMenuAboutUs {
        id
        menus {
          id
          label {
            type
            uri
            article {
              slug
              category {
                ...ArticleCategoryFragment
              }
            }
            category {
              slug
              parentCategory {
                ...Category
                slug
                title {
                  id
                  text_en
                  text_sq
                }
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    title {
                      id
                      text_en
                      text_sq
                    }
                    slug
                    parentCategory {
                      ...Category
                      slug
                      title {
                        id
                        text_en
                        text_sq
                      }
                    }
                  }
                }
              }
            }
            label {
              id
              text_sq
              text_en
            }
          }
          image {
            url
          }
          columns {
            id
            head {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
            body {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
          }
        }
      }
    }
  }
`;
